import { useState } from "react";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { parseApiError } from "../../utils/apiError";

interface IProps {
  /** When provided, the message/correlationId are derived from it via parseApiError. */
  error?: unknown;
  /** Used instead of/alongside `error` when the message is already rendered elsewhere. */
  message?: string;
  correlationId?: string;
}

/** Renders an API error message with a copyable "Support reference" line when a correlationId is available. */
export default function ApiErrorMessage(props: IProps) {
  const { message, correlationId } =
    props.error !== undefined
      ? parseApiError(props.error)
      : { message: props.message, correlationId: props.correlationId };
  const [copied, setCopied] = useState(false);

  if (!message && !correlationId) return null;

  async function handleCopy() {
    if (!correlationId) return;
    try {
      await navigator.clipboard?.writeText(correlationId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard access can fail (e.g. permissions, insecure context) - nothing to recover here.
    }
  }

  return (
    <>
      {message && <Typography>{message}</Typography>}
      {correlationId && (
        <Typography
          variant="caption"
          color="text.secondary"
          component="div"
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          If this keeps happening, please quote this reference when you contact Customer Care:{" "}
          {truncateCorrelationId(correlationId)}
          <Tooltip title={copied ? "Copied" : "Copy reference"}>
            <IconButton size="small" aria-label="copy support reference" onClick={handleCopy}>
              <ContentCopyIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Typography>
      )}
    </>
  );
}

function truncateCorrelationId(id: string): string {
  return id.length > 14 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id;
}
